import { useState, useEffect } from "react";
import type { RegisterAppResponse, AppRegistration } from "../../sw/types";
import { registerApp as registerAppClient } from "../../sw/swClient";
import { isDuneDebugEnabled } from "../utils/debugUtils";

/**
 * Hook to register/update an app in the service worker cache
 */
export const useRegisterApp = (
  registration: AppRegistration | undefined,
  registerAppFunction: typeof registerAppClient = registerAppClient,
): {
  response: RegisterAppResponse | undefined;
  isLoading: boolean;
  error: Error | null;
} => {
  const [response, setResponse] = useState<RegisterAppResponse | undefined>(
    undefined,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isActive = true;

    const performRegister = async () => {
      // Only run if registration is provided
      if (!registration) {
        setResponse(undefined);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const debugEnabled = isDuneDebugEnabled();

        if (debugEnabled) {
          console.log(
            `[Dune Debug] 💾 Starting to cache app in service worker...`,
            {
              appId: registration.appId,
              fileCount: Object.keys(registration.files).length,
            },
          );
        }

        const result = await registerAppFunction(
          registration.baseUrl,
          registration.project,
          registration.appId,
          registration.lastUpdatedTime,
          registration.files,
        );

        if (isActive) {
          setResponse(result);

          if (debugEnabled && result.success) {
            const fileList = Object.keys(registration.files);
            console.log(`[Dune Debug] 🎉 App cached successfully!`, {
              cacheKey: result.cacheKey,
              message: result.message,
              totalFiles: fileList.length,
              allCachedFiles: fileList.sort(),
            });
          }
        }
      } catch (err) {
        if (isActive) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setResponse(undefined);
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    performRegister();

    return () => {
      isActive = false;
    };
  }, [registration, registerAppFunction]);

  return {
    response,
    isLoading,
    error,
  };
};
